import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import CustomTooltip from "src/components/visibility/components/CustomTooltip";
import Iconify from "src/components/iconify";
import { FlagIcon } from "src/components/iconify/flagIcon";
import CustomLineChart from "src/components/inline-chart/inline-chart-activations-scatter";
import { useConquestModeQuery, useUpdateConquestModeMutation } from "src/context/api/non-branded/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import countriesJson from "src/utils/countries.json";

export default function ModalPreview({
    open,
    row,
    onClose,
}: {
    open: boolean;
    row: any;
    onClose: () => void;
}) {
    const [selectedKpi, setSelectedKpi] = useState<string | null>(null);
    const workspace: any = useCurrentWorkspace()
    const [maxCpc, setMaxCpc] = useState<number | "">("");
    const [threshold, setThreshold] = useState<number | "">("");
    const filterStore = useSelector((state: any) => state.filters);

    const [updateConquestMode] = useUpdateConquestModeMutation();

    const { refetch } = useConquestModeQuery({
        workspace_id: workspace?.id,
        client_id: workspace?.bqTable,
        region_name: filterStore?.countries[0]
    },
        {
            skip: !workspace?.id || !filterStore?.countries[0],
            refetchOnMountOrArgChange: true,
        }
    );

    useEffect(() => {
        if (row?.conquest_mode_data?.mode === "Conquest Mode - Paid Top") {
            setSelectedKpi("Paid Top");
        } else if (row?.conquest_mode_data?.mode === "Conquest Mode - Paid First") {
            setSelectedKpi("Paid First");
        }

        if (row?.conquest_mode_data?.threshold != null) {
            setThreshold(row.conquest_mode_data.threshold);
        }


        if (row?.conquest_mode_data?.max_cpc != null) {
            setMaxCpc(row.conquest_mode_data.max_cpc);
        }
    }, [row]);

    const handleSubmit = async () => {
        const updatedData = {
            id: row.conquest_mode_data.id,
            mode:
                selectedKpi === "Paid Top"
                    ? "Conquest Mode - Paid Top"
                    : selectedKpi === "Paid First"
                        ? "Conquest Mode - Paid First"
                        : undefined,
            max_cpc: maxCpc !== "" ? maxCpc : undefined,
            threshold: threshold !== "" ? threshold : undefined,
        };

        try {
            await updateConquestMode({
                workspace_id: workspace?.id,
                data: [updatedData],
            }).unwrap();

            refetch();
            enqueueSnackbar(`Conquest Mode updated successfully`, { variant: "success" });

            onClose();
        } catch (error) {
            console.error("Erreur lors de la mise à jour du Conquest Mode :", error);
            enqueueSnackbar(`Failed to update Conquest Mode`, { variant: "error" });

        }
    };

    return (
        <Dialog
            open={open && !!row}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    color: "#FFF",
                },
            }}
        >
            <DialogTitle sx={{
                padding: "24px",
                backgroundColor: 'rgba(226, 229, 233, 0.16)'
            }}>
                <Box display="flex" justifyContent="space-between">
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                        }}
                    >
                        <Box display="flex" flexDirection="row" gap={2}>
                            <Typography color={"rgba(99, 115, 129, 1)"} fontSize={16}>
                                {row?.ad_group_name}
                            </Typography>
                            <Typography color={"rgba(51, 60, 71, 1)"} fontSize={16}>
                                {row?.keyword}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                gap: 0.8,
                                alignItems: "center",
                            }}
                        >
                            <Box sx={{ position: "relative", top: -3 }}>
                                <FlagIcon
                                    isCircle={false}
                                    code={countriesJson
                                        .find((c) => c.name === row?.region_name)
                                        ?.code?.toLowerCase()}
                                    sx={{ width: 23, height: 16, position: 'relative', top: 2 }}
                                />
                            </Box>

                            <Typography
                                color={"rgba(51, 60, 71, 1)"}
                                sx={{
                                    whiteSpace: "nowrap",
                                    fontSize: 14,
                                }}
                            >
                                {row?.campaign_name}
                            </Typography>

                            <Box
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onClose();
                                }}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 22,
                                    height: 22,
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    color: "rgba(255,255,255,0.6)",
                                    transition: "all 0.15s ease",
                                    "&:hover": {
                                        backgroundColor: "rgba(255,255,255,0.12)",
                                        color: "#FFF",
                                    },
                                }}
                            >
                                <Iconify color={"rgba(94, 109, 123, 1)"} icon="material-symbols:close-rounded" width={16} />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent
                sx={{
                    overflow: "visible",
                    position: "relative",
                    p: 0,
                    backgroundColor: 'white'
                }}
            >
                <Box
                    display="flex"
                    justifyContent="space-between"
                    gap={"18px"}
                    px={'24px'}
                    mb={"20px"}
                    mt={"32px"}
                >
                    {[
                        {
                            title: "Paid Top",
                            data: row?.scatter_plot_top_paid_presence_rate,
                            yField: "top_paid_presence_rate",
                            color: "#FFA726",
                            definition: "How often your domain appears in the paid top ads section.",
                            avg_d1: row?.top_paid_presence_rate_d1
                        },
                        {
                            title: "Paid First",
                            data: row?.scatter_plot_abs_top_paid_presence_rate,
                            yField: "abs_top_paid_presence_rate",
                            color: "#AB47BC",
                            span: "MOST VISIBILITY",
                            bgColorSpan: "rgba(247, 240, 255, 1)",
                            colorBorder: "rgba(226, 229, 233, 0.16)",
                            colorSpan: "rgba(118, 53, 220, 1)",
                            definition: "How often your domain appears in the first position in the paid top ads section.",
                            avg_d1: row?.abs_top_paid_presence_rate_d1
                        },

                    ]
                        .map((chart, i) => {
                            const isSelected = selectedKpi === chart?.title;

                            const xValues = (chart?.data || [])
                                .map((d: any) => Number(d.max_cpc))
                                .filter((v: number) => !isNaN(v));

                            const minX = xValues.length ? Math.min(...xValues) : 0;
                            const maxX = xValues.length ? Math.max(...xValues) : 0;

                            return (
                                <Box
                                    onClick={() => setSelectedKpi(chart.title)}
                                    key={i}
                                    sx={{
                                        flex: 1,
                                        cursor: "pointer",
                                        position: "relative",
                                        background: "#fdfdfd",
                                        border: isSelected
                                            ? "2px solid #469ce9"
                                            : "1px solid rgba(145, 158, 171, 0.12)",
                                        borderRadius: 3,
                                        p: 2.5,
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "space-between",
                                        boxShadow: isSelected
                                            ? "0 0 12px rgba(66, 165, 245, 0.35)"
                                            : "none",
                                        transition: "all 0.2s ease",
                                    }}
                                >
                                    <Box
                                        display="flex"
                                        pb={2}
                                    >
                                        <Box sx={{ width: "100%" }} display={"flex"} flexDirection={"row"} justifyContent={"space-between"}>
                                            <Box display={"flex"} flexDirection={"row"}>
                                                <Box display={"flex"} flexDirection={"row"} gap={1}>
                                                    <Typography color={"rgba(51, 60, 71, 1)"} fontWeight={600}>{chart.title}</Typography>

                                                    <CustomTooltip title={chart?.definition} placement="top">
                                                        <Box sx={{ position: "relative", top: 3 }}>
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 16 16" fill="none">
                                                                <g clip-path="url(#clip0_1134_4695)">
                                                                    <path d="M6 8V6M6 4H6.005M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z" stroke="#919EAB" stroke-opacity="0.6" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                                                                </g>
                                                                <defs>
                                                                    <clipPath id="clip0_1134_4695">
                                                                        <rect width="20" height="20" fill="white" />
                                                                    </clipPath>
                                                                </defs>
                                                            </svg>
                                                        </Box>
                                                    </CustomTooltip>
                                                </Box>

                                            </Box>

                                            {chart?.avg_d1
                                                ? <Box display={"flex"} flexDirection={"row"} gap={'8px'}>
                                                    <Typography color={"rgba(145, 158, 171, 1)"} fontSize={'14px'}>AVG</Typography>
                                                    <Typography color={"rgba(145, 158, 171, 1)"} fontSize={'14px'}>{chart?.avg_d1}%</Typography>
                                                </Box>
                                                : <></>
                                            }
                                        </Box>

                                        {chart.span && (
                                            <Box
                                                sx={{
                                                    bgcolor: chart?.bgColorSpan,
                                                    color: chart?.colorSpan,
                                                    px: 1.5,
                                                    py: 1,
                                                    borderRadius: 1,
                                                    display: "flex",
                                                    border: `1px solid ${chart?.colorBorder}`,
                                                    position: "absolute",
                                                    top: -15,
                                                    right: 20,
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <Typography
                                                    fontWeight={600}
                                                    fontSize={10}
                                                    sx={{ m: 0, lineHeight: 1 }}
                                                >
                                                    {chart.span}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>

                                    <Box
                                        sx={{
                                            height: 250,
                                            background: "#fdfdfd",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {chart?.data?.length > 0 && (
                                            <CustomLineChart
                                                key={chart.title}
                                                data={chart.data}
                                                yField={chart.yField}
                                                minX={minX}
                                                maxX={maxX}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}

                </Box>

                <Box display={"flex"} justifyContent={"flex-end"} gap={2} px={"24px"}>
                    <Box display={"flex"} flexDirection={"row"}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="none">
                            <g filter="url(#filter0_d_2803_11729)">
                                <circle cx="8" cy="8" r="3" fill="rgba(30, 136, 229, 1)" />
                            </g>
                            <defs>
                                <filter id="filter0_d_2803_11729" x="0" y="0" width="22" height="22" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                                    <feFlood flood-opacity="0" result="BackgroundImageFix" />
                                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                    <feMorphology radius="1" operator="dilate" in="SourceAlpha" result="effect1_dropShadow_2803_11729" />
                                    <feOffset />
                                    <feGaussianBlur stdDeviation="2" />
                                    <feComposite in2="hardAlpha" operator="out" />
                                    <feColorMatrix type="matrix" values="0 0 0 0 0.921569 0 0 0 0 0.839216 0 0 0 0 0.992157 0 0 0 0.2 0" />
                                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2803_11729" />
                                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2803_11729" result="shape" />
                                </filter>
                            </defs>
                        </svg>
                        <Typography color={"rgba(30, 136, 229, 1)"} sx={{ opacity: .7 }} position={'relative'} top={2} fontSize={12}>Date & Day</Typography>
                    </Box>
                    <Box display={"flex"} flexDirection={"row"}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="none">
                            <g filter="url(#filter0_d_2803_11729)">
                                <circle cx="8" cy="8" r="3" fill="#7635DC" />
                            </g>
                            <defs>
                                <filter id="filter0_d_2803_11729" x="0" y="0" width="22" height="22" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                                    <feFlood flood-opacity="0" result="BackgroundImageFix" />
                                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                    <feMorphology radius="1" operator="dilate" in="SourceAlpha" result="effect1_dropShadow_2803_11729" />
                                    <feOffset />
                                    <feGaussianBlur stdDeviation="2" />
                                    <feComposite in2="hardAlpha" operator="out" />
                                    <feColorMatrix type="matrix" values="0 0 0 0 0.921569 0 0 0 0 0.839216 0 0 0 0 0.992157 0 0 0 0.2 0" />
                                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2803_11729" />
                                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2803_11729" result="shape" />
                                </filter>
                            </defs>
                        </svg>
                        <Typography color={"rgba(118, 53, 220, 1)"} sx={{ opacity: .8 }} position={'relative'} top={2} fontSize={12}>Previous day (D-1)</Typography>
                    </Box>
                </Box>

                <hr />

                <Box display="flex" alignItems="center" justifyContent={"flex-end"} gap={1.5} pb={3} px={3}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "stretch",
                            width: "180px",
                            height: "32px",
                            borderRadius: "8px",
                        }}
                    >
                        <TextField
                            label="Ceiling Max CPC"
                            type="number"
                            variant="outlined"
                            size="small"
                            value={maxCpc}
                            onChange={(e) => {
                                const val = e.target.value === "" ? "" : Number(e.target.value);
                                if (val === "" || val >= 0) setMaxCpc(val);
                            }}
                            sx={{
                                width: 120,
                                height: 20,
                                "& .MuiInputBase-root.MuiOutlinedInput-root": {
                                    borderRadius: 0,
                                    borderTopLeftRadius: "8px",
                                    borderBottomLeftRadius: "8px",
                                    borderRight: "none",
                                },

                                "& .css-qdrsss-MuiInputBase-input-MuiOutlinedInput-input": {
                                    height: 15,
                                },

                                "& .MuiOutlinedInput-input": {
                                    textAlign: 'right'
                                }
                            }}
                        />
                        <Box
                            sx={{
                                display: "flex",
                                width: "40px",
                                justifyContent: "center",
                                height: 37,
                                alignItems: "center",
                                backgroundColor: "#F2EBFE",
                                borderLeft: "1px solid rgba(255,255,255,0.12)",
                                borderTopRightRadius: "8px",
                                borderBottomRightRadius: "8px",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 14,
                                    color: "rgba(177, 185, 196, 0.90)",
                                }}
                            >
                                €
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "stretch",
                            width: "140px",
                            height: "32px",
                            borderRadius: "8px",
                        }}
                    >
                        <TextField
                            label="Target"
                            type="number"
                            variant="outlined"
                            size="small"
                            value={threshold}
                            onChange={(e) => {
                                const val = e.target.value === "" ? "" : Number(e.target.value);
                                if (val === "" || (val >= 1 && val <= 99)) setThreshold(val);
                            }}
                            sx={{
                                width: 120,
                                height: 20,
                                "& .MuiInputBase-root.MuiOutlinedInput-root": {
                                    borderRadius: 0,
                                    borderTopLeftRadius: "8px",
                                    borderBottomLeftRadius: "8px",
                                    borderRight: "none",
                                },

                                "& .css-qdrsss-MuiInputBase-input-MuiOutlinedInput-input": {
                                    height: 15,
                                },

                                "& .MuiOutlinedInput-input": {
                                    textAlign: 'right'
                                }
                            }}
                        />

                        <Box
                            sx={{
                                display: "flex",
                                width: "40px",
                                justifyContent: "center",
                                height: 37,
                                alignItems: "center",
                                backgroundColor: "#F2EBFE",
                                borderLeft: "1px solid rgba(255,255,255,0.12)",
                                borderTopRightRadius: "8px",
                                borderBottomRightRadius: "8px",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 14,
                                    color: "rgba(177, 185, 196, 0.90)",
                                }}
                            >
                                %
                            </Typography>
                        </Box>
                    </Box>

                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        sx={{
                            background: "white",
                            color: "rgba(51, 60, 71, 1)",
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: 13,
                            px: 2,
                            "&:hover": {
                                background: "rgba(145, 158, 171, 0.50)",
                            },
                        }}
                    >
                        Apply
                    </Button>
                </Box>
            </DialogContent>
        </Dialog >
    );
}